import React, { useState, useCallback, useEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import { useDealActivities, DealActivity } from '../../shared/hooks/useDealActivities';
import { RenderDealActivityData, ActivityPropsType } from '../card/ActivitiesCardHelper';
import ShowEmailReplyModalContainer from '../card/modals/ShowEmailReplyModalContainer';
import ShowSigningPrepMessageModalContainer from '../card/modals/ShowSigningPrepMessageModalContainer';
import { queriesV2 } from '../../api';
import { downloadPdfHelper } from '../document-table/helpers/downloadPdfHelper';

interface DealActivityListProps {
  dealId: string;
  isSidebar?: boolean;
  className?: string;
}

export const DealActivityList: React.FC<DealActivityListProps> = ({ dealId, isSidebar = false, className = '' }) => {
  const apolloClient = useApolloClient();
  const [selectedActivity, setSelectedActivity] = useState<ActivityPropsType | null>(null);
  const [selectedActivityType, setSelectedActivityType] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Handle PDF download when activity type is 'pdf-download'
  useEffect(() => {
    if (selectedActivityType !== 'pdf-download' || !selectedActivity) return;

    const documentId = selectedActivity.object_data?.object_id;
    if (!documentId) {
      setSelectedActivity(null);
      setSelectedActivityType(null);
      return;
    }

    (async () => {
      try {
        const { data } = await apolloClient.query({
          query: queriesV2.getGeneratedDocumentPdf,
          variables: { documentId },
          fetchPolicy: 'network-only',
        });
        const pdfUrl = data?.getDocumentById?.generatedDocumentPdfUrl;
        const docTitle = data?.getDocumentById?.title || 'document';
        if (pdfUrl) {
          await downloadPdfHelper(pdfUrl, docTitle);
        } else {
          console.warn('PDF not available for download');
        }
      } catch (err) {
        console.error('Failed to download PDF from activity:', err);
      } finally {
        setSelectedActivity(null);
        setSelectedActivityType(null);
      }
    })();
  }, [selectedActivityType, selectedActivity, apolloClient]);

  const { activities, loading, error, loadMore, hasMore, totalLoaded } = useDealActivities({
    dealId,
    limit: 25,
  });

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      await loadMore();
    } catch (error) {
      console.error('Error loading more activities:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadMore, loadingMore, hasMore]);

  if (loading && activities.length === 0) {
    return <div className="text-center p-4">Loading activities...</div>;
  }

  if (error) {
    console.error('Error loading deal activities:', error);
    return <div className="text-center p-4">Error loading activities</div>;
  }

  if (!activities.length) {
    return <div className="text-center p-4">No activities yet</div>;
  }

  const handleSetSelectedActivity = (activity: ActivityPropsType | null, activityType?: string) => {
    setSelectedActivity(activity);
    setSelectedActivityType(activityType || null);
  };

  return (
    <>
      <div className={className}>
        {activities.map((activity: DealActivity) => {
          const activityProps: ActivityPropsType & { additional_message?: string } = {
            id: activity.id,
            description: activity.description,
            time: activity.time,
            actor_data: activity.actor_data || {
              full_name: 'System',
              email: '',
            },
            object_data: activity.object_data || {
              object_id: '',
            },
            verb: activity.verb,
            additional_message: activity.additional_message,
          };

          return (
            <div key={activity.id} className={isSidebar ? 'activity-item-mobile' : ''}>
              {RenderDealActivityData(activityProps, isSidebar, handleSetSelectedActivity)}
            </div>
          );
        })}

        {/* Activity count and Load More button */}
        {activities.length > 0 && (
          <div className="text-center p-3">
            <div className="text-muted small mb-2">Showing {totalLoaded} activities</div>
            {hasMore && (
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="btn btn-outline-primary btn-sm"
                style={{ fontSize: '0.875rem' }}
              >
                {loadingMore ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Loading...
                  </>
                ) : (
                  'Load More Activities'
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Keep existing modals */}
      <ShowEmailReplyModalContainer
        isOpen={!!selectedActivity && selectedActivityType === 'email-reply'}
        onClose={() => {
          setSelectedActivity(null);
          setSelectedActivityType(null);
        }}
        activityId={selectedActivity?.object_data?.object_id || null}
      />
      <ShowSigningPrepMessageModalContainer
        isOpen={!!selectedActivity && selectedActivityType === 'signing-prep'}
        onClose={() => {
          setSelectedActivity(null);
          setSelectedActivityType(null);
        }}
        activity={selectedActivity}
      />
      <ShowSigningPrepMessageModalContainer
        isOpen={!!selectedActivity && selectedActivityType === 'signing-prep-canceled'}
        onClose={() => {
          setSelectedActivity(null);
          setSelectedActivityType(null);
        }}
        activity={selectedActivity}
      />
    </>
  );
};
